import { useState, useCallback, useEffect } from "react";
import { userRequest } from "../utils/requestMethods";

/**
 * Shared server-paginated list-fetching pattern: page/limit/total state, a
 * URLSearchParams-built GET request, refetch on any dependency change. Extracted
 * from the page/limit/total boilerplate that was duplicated between
 * ClockAttempts.jsx and TeacherAttendance.jsx (and likely other list pages) —
 * only ClockAttempts.jsx has been migrated to it so far; other pages keep their
 * own inline copy until they're touched for other reasons.
 *
 * @param {string} url - request path, relative to the API base (no leading params)
 * @param {object} options
 * @param {object} [options.params] - extra query params to include on every fetch;
 *   any key with value undefined/null/"all" is omitted (matches the "no filter"
 *   convention used by ClockAttempts.jsx's method/outcome selects)
 * @param {(data: any) => { items: any[], totalDocuments: number, totalPages: number }} options.mapResponse
 *   - maps the raw response body to { items, totalDocuments, totalPages }
 * @param {number} [options.initialLimit=10]
 * @param {any[]} [options.deps=[]] - extra dependencies that should trigger a refetch
 *   (e.g. filter state) beyond page/limit themselves
 * @param {boolean} [options.enabled=true] - fetch is skipped while false (e.g. no schoolId yet)
 */
export const usePaginatedFetch = (
  url,
  { params = {}, mapResponse, initialLimit = 10, deps = [], enabled = true } = {},
) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(initialLimit);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchPage = useCallback(async () => {
    if (!enabled || !url) return;

    try {
      setLoading(true);
      setError("");

      const searchParams = new URLSearchParams();
      searchParams.append("page", page.toString());
      searchParams.append("limit", limit.toString());

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "all") {
          searchParams.append(key, value);
        }
      });

      const { data } = await userRequest.get(`${url}?${searchParams.toString()}`);
      const mapped = mapResponse ? mapResponse(data) : data;

      setItems(mapped?.items || []);
      setTotalDocuments(mapped?.totalDocuments || 0);
      setTotalPages(mapped?.totalPages || 1);
    } catch (err) {
      console.error(`Error fetching ${url}:`, err);
      setError(err.response?.data?.message || "Failed to fetch data.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, page, limit, enabled, ...deps]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const resetToFirstPage = useCallback((newLimit) => {
    if (newLimit !== undefined) setLimit(newLimit);
    setPage(1);
  }, []);

  return {
    items,
    loading,
    error,
    setError,
    page,
    setPage,
    limit,
    setLimit,
    totalDocuments,
    totalPages,
    refetch: fetchPage,
    resetToFirstPage,
  };
};
